/**
 * CommentsThread — task discussion (BIT 9).
 *
 * Visible to all users with task access (RLS enforces).
 * Own messages right-aligned, others left-aligned.
 * No edit/delete after post (BIT 9).
 * Realtime via useComments hook.
 *
 * NB: WhatsApp-style reply (hover to reply, quote, scroll to original)
 * is deferred to Phase 1.5 per Task 5 scope cuts.
 */

import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../api/supabase.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { useComments } from '../../hooks/Usecomments.js';
import Avatar from '../ui/Avatar.jsx';
import Icon from '../ui/Icons.jsx';
import Spinner from '../ui/Spinner.jsx';

function Bubble({ comment, isOwn }) {
  const timeStr = format(new Date(comment.created_at), 'h:mm a');
  return (
    <div className={`flex gap-2.5 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {!isOwn && (
        <Avatar
          name={comment.author?.full_name}
          photoUrl={comment.author?.avatar_url}
          size="sm"
        />
      )}
      <div className={`flex flex-col max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className={`flex items-baseline gap-2 mb-0.5 px-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
          <span className="text-[11px] font-semibold text-ink">
            {isOwn ? 'You' : (comment.author?.full_name || 'Unknown')}
          </span>
          <span className="text-[10px] text-muted">{timeStr}</span>
        </div>
        <div
          className={[
            'rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words leading-relaxed',
            isOwn
              ? 'rounded-tr-sm text-white'
              : 'rounded-tl-sm bg-[var(--surface-muted)] text-ink',
          ].join(' ')}
          style={isOwn ? { background: 'var(--accent)' } : undefined}
        >
          {comment.body}
        </div>
      </div>
    </div>
  );
}

function DateSeparator({ label }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="flex-1 h-px bg-border-base" />
      <span className="text-[10px] uppercase tracking-wider text-muted font-semibold px-1">{label}</span>
      <div className="flex-1 h-px bg-border-base" />
    </div>
  );
}

function groupByDate(comments) {
  const groups = [];
  let lastLabel = null;
  const today = format(new Date(), 'yyyy-MM-dd');
  const yesterday = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
  for (const c of comments) {
    const day = format(new Date(c.created_at), 'yyyy-MM-dd');
    let label = day === today ? 'Today' : day === yesterday ? 'Yesterday' : format(new Date(c.created_at), 'd MMM yyyy');
    if (label !== lastLabel) {
      groups.push({ type: 'separator', label });
      lastLabel = label;
    }
    groups.push({ type: 'comment', comment: c });
  }
  return groups;
}

export default function CommentsThread({ taskId }) {
  const { profile } = useAuth();
  const { comments, loading, error } = useComments(taskId);

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new comments
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const trimmed = body.trim();
    if (!trimmed || !profile?.id || !taskId) return;

    setSending(true);
    const { error: insertError } = await supabase.from('comments').insert({
      task_id: taskId,
      author_id: profile.id,
      body: trimmed,
    });
    setSending(false);

    if (insertError) {
      console.error('[ORIN] Failed to post comment:', insertError);
      setSubmitError(insertError.message || 'Failed to send. Try again.');
      return;
    }
    setBody('');
    // Realtime listener will update the thread.
  };

  const grouped = groupByDate(comments);

  return (
    <div className="card flex flex-col" style={{ height: 'calc(100vh - 200px)', minHeight: '480px' }}>
      {/* Header */}
      <div className="px-5 py-3 border-b border-border-base flex items-center justify-between flex-shrink-0">
        <h3 className="text-sm font-semibold text-ink">
          Comments · <span className="text-muted font-normal">{comments.length}</span>
        </h3>
        <span className="text-[10px] uppercase tracking-wider text-muted font-semibold">
          Live · auto-refreshing
        </span>
      </div>

      {/* Thread */}
      <div
        ref={scrollRef}
        className="flex-1 px-5 py-4 overflow-y-auto flex flex-col gap-3"
      >
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : error ? (
          <p className="text-xs text-status-overdue text-center py-4">{error}</p>
        ) : comments.length === 0 ? (
          <p className="text-xs text-muted text-center py-8">
            No comments yet. Start the conversation below.
          </p>
        ) : (
          grouped.map((item, i) =>
            item.type === 'separator' ? (
              <DateSeparator key={`sep-${i}`} label={item.label} />
            ) : (
              <Bubble
                key={item.comment.id}
                comment={item.comment}
                isOwn={item.comment.author_id === profile?.id}
              />
            )
          )
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-border-base px-3 py-3 flex items-end gap-2 flex-shrink-0"
      >
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a comment…"
          rows={1}
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          className="flex-1 resize-none px-3 py-2 rounded-lg border border-border-base bg-canvas text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2"
          style={{ maxHeight: '120px' }}
        />
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="btn-primary px-3 py-2 disabled:opacity-50 flex-shrink-0"
          aria-label="Send comment"
        >
          <Icon name="send" size={15} />
          <span className="ml-1.5 text-sm">Send</span>
        </button>
      </form>
      {submitError && (
        <p className="text-xs text-status-overdue px-5 pb-2" role="alert">{submitError}</p>
      )}
    </div>
  );
}